import { useState, useEffect, useCallback } from 'react';
import { safeGetJSON, safeSetJSON } from '@/lib/safeStorage';

interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastVisitDate: string | null;
  totalDaysVisited: number;
  visitHistory: string[]; // ISO date strings
  milestones: number[];
}

const STORAGE_KEY = 'human-rooms-streak';

const getDefaultStreakData = (): StreakData => ({
  currentStreak: 0,
  longestStreak: 0,
  lastVisitDate: null,
  totalDaysVisited: 0,
  visitHistory: [],
  milestones: [],
});

const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100, 365];

export const useStreakTracker = () => {
  const [streak, setStreak] = useState<StreakData>(getDefaultStreakData);

  // Load streak data on mount
  useEffect(() => {
    const stored = safeGetJSON<StreakData>(STORAGE_KEY, getDefaultStreakData());
    setStreak(stored);
  }, []);

  // Check and update streak on mount
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    
    setStreak(prev => {
      // Already visited today
      if (prev.lastVisitDate === today) {
        return prev;
      }

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      let newStreak: StreakData;

      if (prev.lastVisitDate === yesterdayStr) {
        // Continuing streak
        const newCurrentStreak = prev.currentStreak + 1;
        const newMilestones = STREAK_MILESTONES.filter(
          m => m <= newCurrentStreak && !prev.milestones.includes(m)
        );
        
        newStreak = {
          ...prev,
          currentStreak: newCurrentStreak,
          longestStreak: Math.max(prev.longestStreak, newCurrentStreak),
          lastVisitDate: today,
          totalDaysVisited: prev.totalDaysVisited + 1,
          visitHistory: [...prev.visitHistory.slice(-365), today],
          milestones: [...prev.milestones, ...newMilestones],
        };
      } else if (prev.lastVisitDate === null) {
        // First visit ever
        newStreak = {
          ...prev,
          currentStreak: 1,
          longestStreak: 1,
          lastVisitDate: today,
          totalDaysVisited: 1,
          visitHistory: [today],
          milestones: [1].filter(m => STREAK_MILESTONES.includes(m)),
        };
      } else {
        // Streak broken
        newStreak = {
          ...prev,
          currentStreak: 1,
          lastVisitDate: today,
          totalDaysVisited: prev.totalDaysVisited + 1,
          visitHistory: [...prev.visitHistory.slice(-365), today],
        };
      }

      safeSetJSON(STORAGE_KEY, newStreak);
      return newStreak;
    });
  }, []);

  const getStreakMessage = useCallback((): string => {
    if (streak.currentStreak === 0) return "Begin your journey";
    if (streak.currentStreak === 1) return "Day one. Again.";
    if (streak.currentStreak < 7) return `${streak.currentStreak} days of presence`;
    if (streak.currentStreak < 14) return "A week of reflection";
    if (streak.currentStreak < 30) return "Two weeks of honesty";
    if (streak.currentStreak < 60) return "A month of looking inward";
    if (streak.currentStreak < 100) return "Two months. Still here.";
    if (streak.currentStreak < 365) return "A hundred days of truth";
    return "A year of being present";
  }, [streak.currentStreak]);

  const hasNewMilestone = useCallback((): boolean => {
    const today = new Date().toISOString().split('T')[0];
    return STREAK_MILESTONES.includes(streak.currentStreak) && 
           streak.lastVisitDate === today;
  }, [streak]);

  return {
    ...streak,
    getStreakMessage,
    hasNewMilestone,
    isFirstVisit: streak.totalDaysVisited === 1,
    daysUntilNextMilestone: STREAK_MILESTONES.find(m => m > streak.currentStreak) 
      ? STREAK_MILESTONES.find(m => m > streak.currentStreak)! - streak.currentStreak 
      : null,
  };
};
